import { Injectable } from '@angular/core';
import { backboneDatabase } from './backbone.db';
import { BackboneTable, VisitDbSyncTaskStatus, VisitUpSyncInfoTable } from './backbone-idb.types';
import { parseDateStringBackBoneVisit } from '@utils/date.utilities';
import { GenericError } from '@models/classes/generic.error';
import { ErrorCode } from '@models/enums/error.codes';
import { VisitStatus } from '@models/types/perform-types/perform-visit.types';
@Injectable({
    providedIn: 'root',
})
export class PerformIdbService {
    constructor() { }

    async addBackboneVisits(visits: BackboneTable[]) {
        for (const visit of visits) {
            const visitExists = await backboneDatabase.backBoneVisits.get(visit.seq)
            if (!visitExists) {
                await backboneDatabase.backBoneVisits.add(
                    visit
                );
            }
        }
    }

    async getSyncFailedVisits(userName: string) {
        let visits = await backboneDatabase.backBoneVisits.where({ userName }).toArray();
        const failedVisits = visits.filter(v => {
            v.syncStatus == VisitDbSyncTaskStatus.DownloadFailed || v.syncStatus == VisitDbSyncTaskStatus.UploadFailed
        });
        return failedVisits;

    }

    async getBackboneVisits(userName: string, excludeOptions?: { exclude: Array<VisitStatus | VisitDbSyncTaskStatus> }): Promise<BackboneTable[]> {
        let visits = await backboneDatabase.backBoneVisits.where({ userName }).toArray();
        visits.sort((visitA, visitB) => {
            const _visitADate = parseDateStringBackBoneVisit(visitA.schedule_end)
            const _visitBDate = parseDateStringBackBoneVisit(visitB.schedule_end)
            const vATime = _visitADate.getTime(), vBTime = _visitBDate.getTime();
            if (vATime > vBTime) {
                return 1;
            } else if (vATime < vBTime) {
                return -1;
            } else {
                return 0
            }
        })

        if (excludeOptions?.exclude) {
            visits = visits.filter(v => !excludeOptions.exclude.includes(v.audit_status));
        }

        return visits;
    }

    async getDownSyncVisitsNo(userName: string): Promise<BackboneTable[]> {
        let visits = await backboneDatabase.backBoneVisits.where({ userName }).toArray();
        visits.sort((visitA, visitB) => {
            const _visitADate = parseDateStringBackBoneVisit(visitA.schedule_end)
            const _visitBDate = parseDateStringBackBoneVisit(visitB.schedule_end)
            const vATime = _visitADate.getTime(), vBTime = _visitBDate.getTime();
            if (vATime > vBTime) {
                return 1;
            } else if (vATime < vBTime) {
                return -1;
            } else {
                return 0
            }
        })

        visits = visits.filter((visit) => (visit.syncStatus === VisitDbSyncTaskStatus.NotDownloaded || visit.syncStatus === VisitDbSyncTaskStatus.DownloadFailed) && visit.audit_status == VisitStatus.CREATED);

        return visits;
    }



    async getScheduleDate(visitNo: string, username: string) {
        const seq = visitNo + username;
        const visit = await backboneDatabase.backBoneVisits.where({ seq }).first();
        if (visit) {
            const date = parseDateStringBackBoneVisit(visit.schedule_start);
            return date;
        } else {
            throw new GenericError({
                errorCode: ErrorCode.NULL_OR_EMPTY,
                message: 'Visit not found with seq ' + seq,
                date: new Date(),
                location: 'perform-idb.service.ts > getScheduleDate()'
            })
        }

    }

    async addUpSyncVisitInfo(visits: VisitUpSyncInfoTable[]) {
        return await backboneDatabase.visitUpSyncInfo.bulkPut(visits, undefined, { allKeys: true });
    }

    async deleteUpSyncVisitInfo(visitNo: string, userName: string) {
        return await backboneDatabase.visitUpSyncInfo.where({ visitNo, userName }).delete();
    }

    async updateVisitSyncStatus(visitSeq: string, lastSyncDate: Date, syncStatus: VisitDbSyncTaskStatus, lastSyncFailedReason?: string): Promise<number>;
    async updateVisitSyncStatus(visitSeq: string[], lastSyncDate: Date, syncStatus: VisitDbSyncTaskStatus, lastSyncFailedReason?: string): Promise<number>;
    async updateVisitSyncStatus(visitSeq: string | string[], lastSyncDate: Date, syncStatus: VisitDbSyncTaskStatus, lastSyncFailedReason?: string): Promise<number> {
        if (typeof visitSeq === 'string') {
            const updated = await backboneDatabase.backBoneVisits.update(visitSeq, { lastSyncDate, syncStatus, lastSyncFailedReason });
            return updated;
        }
        else {
            const updated = await backboneDatabase.backBoneVisits.bulkUpdate(
                visitSeq.map((seq) => ({
                    key: seq,
                    changes: {
                        lastSyncDate,
                        syncStatus,
                        lastSyncFailedReason
                    }
                })),
            );
            return updated;
        }
    }


    async isVisitExistsInBackbone(seq: string) {
        const count = await backboneDatabase.backBoneVisits.where({ seq }).count();
        return count > 0;
    }

    async getUpSyncVisitInfo(visitNo: string, userName: string): Promise<VisitUpSyncInfoTable[]>;
    async getUpSyncVisitInfo(visitNo: string, userName: string, tableNames: string[]): Promise<VisitUpSyncInfoTable[]>;
    async getUpSyncVisitInfo(visitNo: string, userName: string, tableNames?: string[]): Promise<VisitUpSyncInfoTable[]> {
        const visits = await backboneDatabase.visitUpSyncInfo
            .where({
                visitNo,
                userName
            })
            .toArray();
        if (Array.isArray(tableNames) && tableNames.length > 0) {
            return visits.filter((visit) => tableNames.includes(visit.tableName));
        }
        return visits;
    }

    async deleteBackboneVisit(seq: string) {
        return await backboneDatabase.backBoneVisits.delete(seq);
    }

    /**
     * 
     * @param seq backbone visit seq `visitNo+username`
     * @param status New status
     * @returns returns number object stores rows updated
     */
    async updateVisitStatus(seq: string, status: VisitStatus) {
        return await backboneDatabase.backBoneVisits.update(seq, { audit_status: status });
    }


    async getNotDownloadedVisits() {
        const visits = await backboneDatabase.backBoneVisits.where({ syncStatus: VisitDbSyncTaskStatus.NotDownloaded }).toArray();
        return visits;
    }

    async getVisitStatus(visitNo : string, userName : string) :Promise<VisitStatus> {
        const seq = visitNo + userName;
        const visit  = await backboneDatabase.backBoneVisits.get(seq)
        if(visit) {
            return visit.audit_status
        } else {
            throw new GenericError({
                errorCode : ErrorCode.NotFound,
                message : `Visit with key ${seq} not found in backbone db` ,
                location : 'perform-idb.service>getVisitStatus()'
            })
        }
    }

}
